import { useLayoutEffect, useEffect, useState, useContext, useRef } from 'react';
import { useSelector as useSelectorUtil, shallowEqual, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';

import AppContext from './context';
// eslint-disable-next-line import/no-cycle
import { JobCandidatesDataContext } from '../hoc/withJobCandidatesData';

import {
  getCurrentEmployer,
  getEmployerGrantsById,
  getJobcaseEmployerAdminLevel,
} from '../redux/nodes/features/employers/selectors';
import settingsSelectors from '../redux/nodes/features/settings/selectors';
import settingsActions from '../redux/nodes/features/settings/actions';
import hiringEventsSelectors from '../redux/nodes/entities/hiringEvents/selectors';
import hiringEventsActions from '../redux/nodes/entities/hiringEvents/actions';

import { EMPLOYER_ROLE_ACCESS_LEVELS } from '../constants';
import { ENTITIES_TYPE } from './constants/mediaService';
import { hasAdminWriteAccessForSettings, hasWriteAccessForSettings } from './grants';
import { isModuleViewable } from './routing';

const useSelector = selector => useSelectorUtil(selector, shallowEqual);

const useEffectAsync = (hook, args, cleanup) => {
  useEffect(() => {
    (async () => {
      await hook();
    })();
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, args);
};

const useEffectAfterMount = (hook, deps) => {
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isMountedRef.current) {
      hook();
    } else {
      isMountedRef.current = true;
    }
  }, deps);
};

// Use this hook if you are setting a custom breakpoint,
// otherwise use isMobile from the context with the useAppContext hook.
// Initial value of isMobile is set via user agent sniffing, and then
// can be modified with the window width check.
const useIsMobile = (mobileBreakpoint = 768, mobileUA = true) => {
  const { isMobileUA } = useContext(AppContext);

  const checkMobileWidth = mobileBreakpoint => window.innerWidth < mobileBreakpoint;

  const [isMobile, setIsMobile] = useState((() => {
    if (typeof window === 'undefined') return isMobileUA || mobileUA;
    return checkMobileWidth(mobileBreakpoint);
  })());

  useEffect(() => {
    const handleDisplayResize = debounce(() => {
      setIsMobile(checkMobileWidth(mobileBreakpoint));
    }, 15);

    window.addEventListener('resize', handleDisplayResize);

    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('resize', handleDisplayResize);
    };
  }, [mobileBreakpoint]);

  return isMobile;
};

// Gives access to the application context without having to import useContext and AppContext everywhere.
const useAppContext = () => useContext(AppContext);

const usePrevious = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

const useCombinedRefs = (...refs) => {
  const targetRef = useRef();

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        // eslint-disable-next-line no-param-reassign
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const useIntersect = ({
  root = null,
  rootMargin,
  threshold = 0,
}) => {
  const [entry, updateEntry] = useState({});
  const [node, setNode] = useState(null);

  const observer = useRef(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      ([entry]) => updateEntry(entry),
      { root, rootMargin, threshold },
    );

    const { current: currentObserver } = observer;

    if (node) currentObserver.observe(node);

    return () => currentObserver.disconnect();
  }, [node, root, rootMargin, threshold]);

  return [setNode, entry];
};

const useStateWithCallback = (initialState) => {
  const [state, setState] = useState(initialState);
  return [
    state,
    (value, callback) => {
      setState(value);
      // This is done so that the callback is called after the state has been updated,
      // the component has been re-rendered and the event loop is run again by the JS engine.
      if (callback) setTimeout(() => callback());
    },
  ];
};

export const useClickBackForCreateOverlay = (callback, args) => {
  const [isVisible] = args;
  useEffect(() => {
    if (isVisible) {
      window.addEventListener('popstate', callback);
    }
    return () => {
      window.removeEventListener('popstate', callback);
    };
  }, args);

  useEffect(() => {
    if (isVisible) {
      window.history.pushState(null, null, window.location.href);
    }
  }, [isVisible]);
};

export const useCheckAccessLevelForRole = (accessLevel, callback) => {
  const currentEmployerId = useSelector(getCurrentEmployer);
  const employerGrants = useSelector(getEmployerGrantsById(currentEmployerId));
  const jobcaseEmployerAdminLevel = useSelector(getJobcaseEmployerAdminLevel);
  const { READ, WRITE, ADMIN_READ, ADMIN_WRITE } = EMPLOYER_ROLE_ACCESS_LEVELS;

  let hasSuperAdminAccess = false;
  switch (accessLevel) {
    case READ:
      hasSuperAdminAccess = jobcaseEmployerAdminLevel === READ || jobcaseEmployerAdminLevel === ADMIN_READ;
      break;
    case WRITE:
      hasSuperAdminAccess = jobcaseEmployerAdminLevel === WRITE || jobcaseEmployerAdminLevel === ADMIN_WRITE;
      break;
    case ADMIN_READ:
      hasSuperAdminAccess = jobcaseEmployerAdminLevel === ADMIN_READ;
      break;
    case ADMIN_WRITE:
      hasSuperAdminAccess = jobcaseEmployerAdminLevel === ADMIN_WRITE;
      break;
    default:
      hasSuperAdminAccess = false;
  }

  const hasAccess = hasSuperAdminAccess || callback(employerGrants);

  return hasAccess;
};

export const useCheckAccessLevelForModule = (module) => {
  const currentEmployerId = useSelector(getCurrentEmployer);
  const employerGrants = useSelector(getEmployerGrantsById(currentEmployerId));
  const hasAccess = isModuleViewable(employerGrants, module);
  return hasAccess;
};

const useJobCandidatesData = () => useContext(JobCandidatesDataContext);

export const useWindowDimensions = () => {
  const hasWindow = typeof window !== 'undefined';

  function getWindowDimensions() {
    const width = hasWindow ? window.innerWidth : null;
    const height = hasWindow ? window.innerHeight : null;
    return { width, height };
  }

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    const handleResize = () => {
      if (hasWindow) {
        setWindowDimensions(getWindowDimensions());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [hasWindow]);

  return windowDimensions;
};

const useOrgMarketingAssets = () => {
  const dispatch = useDispatch();

  const currentEmployerId = useSelector(getCurrentEmployer);

  const params = {
    desc_sort: true,
    page_index: 1,
    page_size: 9999,
    sort_field: 'createdAt',
  };
  return {
    isLoading: useSelector(settingsSelectors.getMarketingIsLoading),
    assets: Object.values(useSelector(settingsSelectors.getMarketingData)),
    fetchAssets: (assetType) => {
      if (assetType) {
        params.asset_type = assetType;
      }
      dispatch(settingsActions.getOrganizationAssetsDetail(currentEmployerId, params));
    },
    createAssets: async (data) => {
      await dispatch(settingsActions.createOrganizationAssets(currentEmployerId, {
        data: {
          asset_type: data?.asset_type,
          media_id: data?.media_id,
        },
      }));
    },
    editAssets: async (data) => {
      await dispatch(settingsActions.editOrganizationAssets(currentEmployerId, data?.id, {
        data: {
          asset_type: data?.asset_type,
          media_id: data?.media_id,
        },
      }));
    },
    deleteAssets: async (data) => {
      await dispatch(settingsActions.deleteOrganizationAssets(currentEmployerId, data?.id));
    },
  };
};

const useSubOrgMarketingAssets = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const currentEmployerId = useSelector(getCurrentEmployer);

  const subOrgId = router?.query?.id;
  const params = {
    desc_sort: true,
    page_index: 1,
    page_size: 9999,
    sort_field: 'createdAt',
  };

  return {
    isLoading: useSelector(settingsSelectors.getSubOrgMarketingIsLoading),
    assets: Object.values(useSelector(settingsSelectors.getSubOrgMarketingData)),
    fetchAssets: (assetType) => {
      if (assetType) {
        params.asset_type = assetType;
      }
      dispatch(settingsActions.getSubOrganizationAssets(currentEmployerId, subOrgId, params));
    },
    createAssets: async (data) => {
      await dispatch(settingsActions.createSubOrganizationAssets(currentEmployerId, subOrgId, {
        data: {
          asset_type: data?.asset_type,
          media_id: data?.media_id,
        },
      }));
    },
    editAssets: async (data) => {
      await dispatch(settingsActions.editSubOrganizationAssets(currentEmployerId, subOrgId, data?.id, {
        data: {
          asset_type: data?.asset_type,
          media_id: data?.media_id,
        },
      }));
    },
    deleteAssets: async (data) => {
      await dispatch(settingsActions.deleteSubOrganizationAssets(currentEmployerId, subOrgId, data?.id));
    },
  };
};

const useHiringEventAssets = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const hiringEventId = router?.query?.id;

  const eventDetail = useSelector(hiringEventsSelectors.getEventById(hiringEventId));
  const isLoading = useSelector(hiringEventsSelectors.getIsLoading);

  const assets = eventDetail?.images || [];

  return {
    isLoading,
    assets,
    fetchAssets: async () => {
      await dispatch(hiringEventsActions.getEvent({ eventId: hiringEventId }));
    },
    createAssets: async (data) => {
      let updatedAssets = assets.map(asset => ({
        display_order: asset.display_order,
        image_type: asset.image_type,
        image_url: asset?.image_url,
        hiring_event_id: asset.hiring_event_id,
      })) || [];

      updatedAssets.push({
        display_order: updatedAssets.length + 1,
        image_type: ENTITIES_TYPE.PROMOTIONAL,
        image_url: data?.url,
        hiring_event_id: hiringEventId,
      });

      updatedAssets = updatedAssets.map((asset, index) => ({
        ...asset,
        display_order: index + 1,
      }));

      await dispatch(hiringEventsActions.updateHiringEvent({ eventId: hiringEventId, data: { images: updatedAssets } }, true));
    },
    editAssets: () => {},
    deleteAssets: async (data) => {
      const updatedAssets = assets.filter(asset => asset.id !== data.id).map((asset, index) => ({
        display_order: index + 1,
        image_type: asset.image_type,
        image_url: asset?.image_url,
        hiring_event_id: asset.hiring_event_id,
      }));
      await dispatch(hiringEventsActions.updateHiringEvent({ eventId: hiringEventId, data: { images: updatedAssets } }, true));
    },
  };
};

const useMarketingAssets = (type) => {
  const hasWriteAccess = useCheckAccessLevelForRole(EMPLOYER_ROLE_ACCESS_LEVELS.WRITE, hasWriteAccessForSettings);
  const hasAdminWriteAccess = useCheckAccessLevelForRole(EMPLOYER_ROLE_ACCESS_LEVELS.ADMIN_WRITE, hasAdminWriteAccessForSettings);

  const data = {};
  data.hiring_events_assets = useHiringEventAssets();
  data.org_marketing_assets = useOrgMarketingAssets();
  data.sub_org_marketing_assets = useSubOrgMarketingAssets();
  return {
    ...data[type],
    hasWriteAccess: hasAdminWriteAccess || hasWriteAccess,
  };
};

export {
  useIsomorphicLayoutEffect as useLayoutEffect,
  useSelector,
  useEffectAsync,
  useEffectAfterMount,
  useIsMobile,
  useAppContext,
  usePrevious,
  useCombinedRefs,
  useIntersect,
  useStateWithCallback,
  useJobCandidatesData,
  useMarketingAssets,
};

export default {
  useLayoutEffect: useIsomorphicLayoutEffect,
  useSelector,
  useEffectAsync,
  useEffectAfterMount,
  useIsMobile,
  useAppContext,
  usePrevious,
  useCombinedRefs,
  useIntersect,
  useStateWithCallback,
  useJobCandidatesData,
  useWindowDimensions,
  useMarketingAssets,
};
